import React, { useState } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { User } from '@/lib/types';

interface AddPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddPlayer: (player: { user_id: string; name: string; email?: string; default_buyin_method?: 'digital' | 'cash' }) => void;
  availableUsers: User[];
}

export function AddPlayerModal({ isOpen, onClose, onAddPlayer, availableUsers }: AddPlayerModalProps) {
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [search, setSearch] = useState('');

  const filteredUsers = availableUsers.filter(u => 
    u.name.toLowerCase().includes(search.toLowerCase()) || 
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddGuest = () => {
    if (!guestName) return;
    onAddPlayer({
      user_id: `guest_${Date.now()}`,
      name: guestName,
      email: guestEmail || undefined
    });
    setGuestName('');
    setGuestEmail('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="ADD PLAYER">
      <div className="space-y-6 py-2">
        {/* Registered Players Section */}
        <div className="space-y-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-aura-text/40">
            Registered Players
          </div>
          
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-xl bg-aura-background/50 border-aura-text/10 text-sm"
          />
          
          <div className="max-h-[200px] overflow-y-auto space-y-1 pr-1 custom-scrollbar">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-aura-text/40 text-xs font-bold uppercase tracking-widest">
                No users found
              </div>
            ) : (
              filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => {
                    onAddPlayer({
                      user_id: user.id,
                      name: user.name,
                      email: user.email || undefined,
                      default_buyin_method: user.default_buyin_method,
                    });
                    onClose();
                  }}
                  className="w-full flex items-center gap-3 p-2 bg-white hover:bg-aura-background rounded-xl border-transparent hover:border-aura-primary/10 shadow-sm transition-all text-left group"
                >
                  <Avatar name={user.name} photoUrl={user.photo_url} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-aura-text text-[14px] group-hover:text-aura-primary transition-colors truncate">{user.name}</div>
                    {user.email && <div className="text-[11px] text-aura-text/40 truncate">{user.email}</div>}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
        
        <div className="h-px bg-aura-text/5" />
        
        {/* Add Guest Section */}
        <div className="space-y-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-aura-text/40">
            Add Guest
          </div>
          
          <div className="grid gap-3">
            <div className="space-y-1">
              <label className="text-[9px] font-black uppercase text-aura-text/40 ml-1">Player Name</label>
              <Input
                placeholder="Enter name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className="rounded-xl bg-aura-background/50 border-aura-text/10 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-black uppercase text-aura-text/40 ml-1">Email (Optional)</label>
              <Input
                placeholder="Enter email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="rounded-xl bg-aura-background/50 border-aura-text/10 text-sm"
              />
            </div>
            <Button
              variant="primary"
              fullWidth
              onClick={handleAddGuest}
              disabled={!guestName}
              className="rounded-full py-4 h-auto font-black uppercase tracking-widest mt-2 shadow-lg shadow-aura-primary/20 bg-gradient-to-r from-[#006855] to-[#005b4a] text-white"
            >
              Add Guest
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Avatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  return (
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#006855] to-[#005b4a] text-white flex items-center justify-center overflow-hidden shrink-0 border border-aura-primary/5">
      {photoUrl ? (
        <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span className="text-white font-black text-xs">{name.charAt(0)}</span>
      )}
    </div>
  );
}

